<template>
  <div v-if="inProgressAction">
    <LoadingSpinner size="small" />
  </div>

  <div v-else-if="createPhase == 'justSubmitted'">
    <div class="p-2">
      Thank you! Please check back here when you receive a response
    </div>
  </div>

  <div v-else-if="application">

    <div v-if="currentStatus?.statusId == 'NEW'">
      <p>Please let us know whether you were able to finish submitting your application via SSA.gov</p>

      <button class="ctaButton m-2" @click="submitEvent(ApplicationEvent.APPLICATION_SUBMITTED)">
        <i class="fa fa-check mr-1"></i>
        Yes, I submitted it
      </button>

      <button class="ctaButton m-2" @click="submitEvent(ApplicationEvent.APPLICATION_NOT_SUBMITTED, true); showApplicationTypePrompt();">
        <i class="fa fa-times mr-1"></i>
        No, please take me back to SSA.gov
      </button>

    </div>

    <div v-else-if="currentStatus?.statusId == 'SUBMITTED'">
      <p>Please let us know whether your application was approved</p>
      <button class="ctaButton m-2" @click="submitEvent(ApplicationEvent.APPLICATION_APPROVED)">
        <i class="fa fa-check mr-1"></i>
        Yes, it was approved
      </button>
      <button class="ctaButton m-2" @click="submitEvent(ApplicationEvent.APPLICATION_REJECTED)">
        <i class="fa fa-times mr-1"></i>
        No, it was denied
      </button>
      <button class="ctaButton m-2" @click="createPhase = 'justSubmitted'">
        <i class="fa fa-clock mr-1"></i>
        I'm still waiting
      </button>
    </div>

    <div v-else-if="currentStatus?.statusId == 'REJECTED'">
      <p>Would you like to try applying again?</p>
      <button class="ctaButton m-2" @click="submitEvent(ApplicationEvent.REAPPLY, true); showApplicationTypePrompt();">
        <i class="fa fa-pen mr-1"></i>
        Apply again
      </button>
    </div>

  </div>

  <button v-else class="ctaButton startButton" @click="createPhase='chooseType'">
    <i class="fa fa-pen mr-1"></i>
    Start Application
  </button>

  <ModalDialog v-if="createPhase=='chooseType'" :active="true" class="modal"
    title="Social Security Application" severity="info"
    :allowClose="true" @close="createPhase='none'"
    firstButtonText="I need a new SS number" firstButtonSeverity="info" @firstbuttonselected="onApplicationTypeSelected(false)"
    secondButtonText="I just need a replacement card" secondButtonSeverity="info" @secondbuttonselected="onApplicationTypeSelected(true)"
  >
    <p>If you don't have a Social Security number at all, you can apply for one.</p>
    <p class="mt-3">If you know your number, but don't have a card, you can order a replacement.</p>
    <p class="mt-3"><strong>We will send you to the official SSA.gov site to complete the application.</strong></p>
  </ModalDialog>

  <ModalDialog v-if="createPhase=='chooseAuthMethod'" :active="true" class="modal"
    title="Social Security Application" severity="info"
    :allowClose="true" @close="createPhase='none'"
    firstButtonText="I have an account" firstButtonSeverity="info" @firstbuttonselected="onAuthTypeSelected(true)"
    secondButtonText="I do not have an account" secondButtonSeverity="info" @secondbuttonselected="onAuthTypeSelected(false)"
  >
    <p>If you have an existing account with <strong>ID.me</strong>, <strong>Login.gov</strong>, or <strong>SSA.gov</strong>, then we can speed up the application process.</p>
    <p class="mt-3">If you don't have an existing account, you can still apply.</p>

    <div class="addressMessage mt-4 is-flex is-flex-direction-row full-width">
      <i class="fas fa-envelope mt-1 mr-2"></i>
      <div class="full-width">
        <strong>Please use this email when filling out the application:</strong>
        <p style="text-align: center; font-size: 125%" class="mt-3 mb-1">{{ `hello+${sessionState.session!.user.id}@minicityatl.com` }}</p>
      </div>
    </div>

    <div class="addressMessage mt-4 is-flex is-flex-direction-row full-width">
      <i class="fas fa-map-pin mt-1 mr-2"></i>
      <div class="full-width">
        <strong>Please use this address when filling out the application:</strong>
        <p style="text-align: center; font-size: 125%" class="mt-3 mb-3">{{ ProfileTypes.formatUSAddressShort(CONFIG?.networkMailingAddress) }}</p>
        <strong><u>DO NOT</u> use this address for anything other than your Social Security application. Any other mail received here will not be delivered</strong>
      </div>
    </div>
  </ModalDialog>

  <ModalDialog v-if="createPhase=='awaitResponse'" :active="true" class="modal"
    title="Social Security Application" severity="info"
    firstButtonText="I completed the application" firstButtonSeverity="info" @firstbuttonselected="onApplicationSubmitted()"
    secondButtonText="I wasn't able to finish" secondButtonSeverity="info" @secondbuttonselected="submitEvent(ApplicationEvent.APPLICATION_NOT_SUBMITTED)"
  >
    <p class="mt-3"><strong>Please let us know whether you were able to complete your application.</strong></p>

    <div class="addressMessage mt-4 is-flex is-flex-direction-row full-width">
      <i class="fas fa-envelope mt-1 mr-2"></i>
      <div class="full-width">
        <strong>Please use this email when filling out the application:</strong>
        <p style="text-align: center; font-size: 125%" class="mt-3 mb-1">{{ `hello+${sessionState.session!.user.id}@minicityatl.com` }}</p>
      </div>
    </div>

    <div class="addressMessage mt-4 is-flex is-flex-direction-row full-width">
      <i class="fas fa-map-pin mt-1 mr-2"></i>
      <div class="full-width">
        <strong>Please use this address when filling out the application:</strong>
        <p style="text-align: center; font-size: 125%" class="mt-3 mb-3">{{ ProfileTypes.formatUSAddressShort(CONFIG?.networkMailingAddress) }}</p>
        <strong><u>DO NOT</u> use this address for anything other than your Social Security application. Any other mail received here will not be delivered</strong>
      </div>
    </div>
  </ModalDialog>
</template>

<script lang="ts" setup>
import { ApplicationStatus, ProfileTypes } from '@minicity/core';
import { computed, ref } from 'vue';
import { submitApplication, submitApplicationEvent, ApplicationEvent } from "@/features/applications/onlineSocialSecurity/api";
import { sessionState } from '@/core/session';
import { doApiAction } from '@/core/api';
import { CONFIG } from '@/core/config';

const props = defineProps<{
  application?: ApplicationStatus.Report
}>();

const emit = defineEmits(['changed']);

const currentStatus = computed(() => {
  if (!props.application) return null;

  let status: ApplicationStatus.HistoryEntry|null = null;
  for (let entry of props.application.history) {
    if (entry.type != "FUTURE") { status = entry; }
  }
  return status;
});


const inProgressAction = ref<string|null>(null);

const createPhase = ref<"none"|"chooseType"|"chooseAuthMethod"|"awaitResponse"|"justSubmitted">("none");
const isForReplacementCard = ref<boolean>(false);
const hasExistingLogin = ref<boolean>(false);
const createdApplicationId = ref<string|null>(null);

const showApplicationTypePrompt = () => {
  createPhase.value = "chooseType";
}

const onApplicationTypeSelected = (replacement: boolean) => {
  isForReplacementCard.value = replacement;
  createPhase.value = "chooseAuthMethod";
}

const onAuthTypeSelected = (hasLogin: boolean) => {
  hasExistingLogin.value = hasLogin;
  
  window.open(hasLogin ? 'https://secure.ssa.gov/RIL/SiView.action' : 'https://secure.ssa.gov/ossnap/public/landingOSsnap', '_blank');

  if (props.application || createdApplicationId.value) {
    // Retrying an existing application
    submitEvent(ApplicationEvent.REAPPLY, true);
    createPhase.value = "awaitResponse";
  } else {
    // New application
    doApiAction(
      inProgressAction, "startApplication",
      null, null,
      () => submitApplication({
        citizenId: sessionState.session!.user.id,
        isForReplacementCard: isForReplacementCard.value,
        idempotencyToken: Math.random().toString(),
      })
      .then((id) => {
        createdApplicationId.value = id;
        createPhase.value = "awaitResponse";
      })
    );
  }
}

const onApplicationSubmitted = () => {
  submitEvent(ApplicationEvent.APPLICATION_SUBMITTED, true);
  createPhase.value = "justSubmitted";
}

const submitEvent = (event: ApplicationEvent, suppressChangedEvent?: boolean) => {
  doApiAction(
    inProgressAction, "submitEvent",
    null, null,
    () => submitApplicationEvent(
      props.application ? props.application.id : createdApplicationId.value!,
      event
    ).then(() => { if (!suppressChangedEvent) { emit('changed') } })
  );
}
</script>

<style lang="scss" scoped>
.startButton {
  width: 100%;
}

.modal {
  text-align: left;
}

.addressMessage {
  padding: 1rem;
  background-color: #c5fcf7;
  border-radius: 1rem;
}
</style>
